/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCategories, useColors, useSizeGuides, useMaterials, useCollections, useOccasions } from '../../../features/products/hooks/useMetaData'
import { productsApi } from '../../../features/products/api/productsApi'
import type { PropertyData, FormData, ProductMediaItem } from '../../AddProductPage/types'
import type { Product, ProductStatus } from '../../../features/products/types'
import { transformToUpdateFormat } from '../utils/dataTransform'
import { fetchProductTaxonomy, type ProductTaxonomyLinks } from '../utils/productTaxonomy'
import { isSaleModeValid } from '../../AddProductPage/utils/saleMode'
import { UnresolvedMetadataError } from '../../../features/products/utils/productPayload'
import { useToast } from '../../../shared/components/toast/useToast'
import { DEFAULT_STOCK_UP_TRIGGER, triggerOf, type StockLeaf } from '../../../features/products/utils/stockStatus'

// PE-2169 — the threshold is stored per leaf but edited once per product; the
// first leaf is representative because dataTransform stamps the same value on all.
const firstLeafStockUpTrigger = (leaf: StockLeaf | undefined): string => String(triggerOf(leaf ?? {}))

// PE-1283 §7 E4 — hydrate the gallery from a loaded product's media rows. The BFF
// outbound contract lower-cases the type (image|video) and may expose the poster
// as `poster` or `posterUrl`; normalise both into the form's ProductMediaItem.
let hydrateCounter = 0
const hydrateProductMedia = (raw: unknown): ProductMediaItem[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .map((m: any, index: number): ProductMediaItem | null => {
      if (!m?.url) return null
      const type = String(m.type).toUpperCase() === 'VIDEO' ? 'VIDEO' : 'IMAGE'
      const rawStatus = String(m.status ?? 'READY').toUpperCase()
      const status: ProductMediaItem['status'] =
        rawStatus === 'PROCESSING' ? 'PROCESSING' : rawStatus === 'FAILED' ? 'FAILED' : 'READY'
      return {
        localId: `hydrated_${hydrateCounter++}`,
        mediaId: m.id ?? m.mediaId ?? 'persisted',
        url: m.url,
        type,
        posterUrl: m.posterUrl ?? m.poster ?? undefined,
        sortOrder: typeof m.sortOrder === 'number' ? m.sortOrder : index,
        status,
      }
    })
    .filter((m): m is ProductMediaItem => m !== null)
}

export const useEditProduct = (productId: string, productStatus?: ProductStatus) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: categoriesData } = useCategories()
  const { data: colorsData } = useColors()
  const { data: sizeGuideData } = useSizeGuides()
  const { data: materialsData } = useMaterials()
  // PE-2087 — many-to-many taxonomy option sources (product-service id namespace).
  const { data: collectionsData } = useCollections()
  const { data: occasionsData } = useOccasions()

  const [product, setProduct] = useState<Product | null>(null)
  const [isLoadingProduct, setIsLoadingProduct] = useState(true)

  const [formData, setFormData] = useState<FormData>({
    name: '',
    category: '',
    details: '',
    price: '',
    baseCostPrice: '',
    productionTimeInDays: '',
    styleGuide: '',
    quantity: 0,
    images: [],
    discountPercentage: 0,
    media: [],
    saleMode: 'PRICE',
    bookingServiceId: '',
    stockUpTrigger: String(DEFAULT_STOCK_UP_TRIGGER),
    collectionIds: [],
    categoryIds: [],
    occasionIds: [],
  })

  // PE-2087 — the product's CURRENT taxonomy links, read from the three
  // /products/:id/{collections,categories,occasions} routes. Held in a ref as well so the
  // product-prefill effect below (which rebuilds formData wholesale) can't blank them,
  // whichever of the two requests settles first.
  const linkedTaxonomyRef = useRef<ProductTaxonomyLinks>({
    collectionIds: [],
    categoryIds: [],
    occasionIds: [],
  })

  const [propertyData, setPropertyData] = useState<PropertyData>({
    sizes: []
  })

  const [selectedSize, setSelectedSize] = useState('')
  const [selectedMaterial, setSelectedMaterial] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)


  // Fetch product by ID
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoadingProduct(true)
        const status = productStatus || 'PUBLISHED'
        const productData = await productsApi.getByIdAndStatus(productId, status)
        setProduct(productData)
      } catch (error) {
        console.error('Error fetching product:', error)
      } finally {
        setIsLoadingProduct(false)
      }
    }

    if (productId) {
      fetchProduct()
    }
  }, [productId, productStatus])

  // PE-2087 — load the product's existing collection/occasion/category-tag links. Applied
  // once, on arrival, so later merchant edits to the pickers are never clobbered.
  useEffect(() => {
    if (!productId) return
    let cancelled = false
    fetchProductTaxonomy(productId)
      .then((links) => {
        if (cancelled) return
        linkedTaxonomyRef.current = links
        setFormData((prev) => ({ ...prev, ...links }))
      })
      .catch((error) => console.error('Error fetching product taxonomy:', error))
    return () => {
      cancelled = true
    }
  }, [productId])

  // Pre-fill form data when product loads
  useEffect(() => {
    if (product && sizeGuideData) {
      // Handle both published and draft product schemas
      const isDraft = product.status === "DRAFT";

      if (isDraft) {
        // Draft product schema
        const draftData = (product as any).data;

        setFormData({
          name: product.name,
          category: draftData?.categoryId || "",
          details: draftData?.description || "",
          price: draftData?.basePrice?.toString() || "",
          baseCostPrice: draftData?.baseCostPrice?.toString() || "",
          productionTimeInDays: product.productionTimeInDays?.toString() || "",
          styleGuide: draftData?.sizeGuideId || "",
          quantity: 0,
          images: [],
          discountPercentage: draftData?.discountPercentage || 0,
          media: hydrateProductMedia(draftData?.media ?? (product as any).media),
          // PE-2099 — products predating the sale-mode toggle come back without
          // saleMode; rack-api treats that as PRICE, so mirror it here.
          saleMode: draftData?.saleMode ?? product.saleMode ?? 'PRICE',
          bookingServiceId: draftData?.bookingServiceId ?? product.bookingServiceId ?? '',
          // PE-2169 — one product-level threshold; hydrate from the first leaf.
          stockUpTrigger: firstLeafStockUpTrigger(draftData?.sizes?.[0]?.materials?.[0]?.colours?.[0]),
          ...linkedTaxonomyRef.current,
        });

        // Transform draft data to property data format
        const transformedPropertyData = {
          sizes:
            draftData?.sizes?.map((sizeItem: any) => ({
              sizeId: sizeItem.sizeId,
              additionalCostPrice: sizeItem.additionalCostPrice || 0,
              price: sizeItem.additionalPrice || 0,
              materials:
                sizeItem.materials?.map((material: any) => ({
                  name:
                    materialsData?.data?.find(
                      (m: any) => m.id === material.materialId,
                    )?.name || "",
                  additionalCostPrice: material.additionalCostPrice || 0,
                  price: material.additionalPrice || 0,
                  colors:
                    material.colours?.map((colour: any) => ({
                      id: colour.colourId,
                      name:
                        colorsData?.data?.find(
                          (c: any) => c.id === colour.colourId,
                        )?.name || "",
                      hex:
                        colorsData?.data?.find(
                          (c: any) => c.id === colour.colourId,
                        )?.hexValue || "",
                      additionalCostPrice: colour.additionalCostPrice || 0,
                      price: colour.additionalPrice || 0,
                      quantity: colour.quantity || 0,
                      weight: colour.weight || 0,
                      images: [],
                      imageUrls: colour.imageUrls || [],
                    })) || [],
                })) || [],
            })) || [],
        };
        setPropertyData(transformedPropertyData);

        // Set initial selections for draft
        if (transformedPropertyData?.sizes?.length > 0) {
          setSelectedSize(transformedPropertyData.sizes[0].sizeId);
          if (transformedPropertyData.sizes[0].materials.length > 0) {
            setSelectedMaterial(
              transformedPropertyData.sizes[0].materials[0].name,
            );
            if (
              transformedPropertyData.sizes[0].materials[0].colors.length > 0
            ) {
              setSelectedColor(
                transformedPropertyData.sizes[0].materials[0].colors[0].hex,
              );
            }
          }
          setCurrentStep(2);
        }
      } else {
        // Published product schema (existing logic)
        const sizeGuide =
          product?.sizeGuide ||
          sizeGuideData?.result?.data?.find((sg) =>
            product?.sizes?.some((size) => size.size.sizeId === sg.id),
          );

        setFormData({
          name: product?.name,
          category: product?.categoryId,
          details: product?.description,
          price: product?.basePrice?.toString(),
          baseCostPrice: product?.baseCostPrice?.toString() || "",
          productionTimeInDays: product?.productionTimeInDays?.toString() || "",
          styleGuide: sizeGuide?.id || product?.sizeGuide?.id || "",
          quantity: 0,
          images: [],
          discountPercentage: (product as any).discountPercentage || 0,
          media: hydrateProductMedia((product as any).media),
          saleMode: product.saleMode ?? 'PRICE',
          bookingServiceId: product.bookingServiceId ?? '',
          stockUpTrigger: firstLeafStockUpTrigger(product.sizes?.[0]?.materials?.[0]?.colours?.[0]),
          ...linkedTaxonomyRef.current,
        });

        // Transform published product data to property data format
        const transformedPropertyData = {
          sizes: product?.sizes?.map((sizeItem) => ({
            sizeId: sizeItem?.size.id,
            additionalCostPrice: sizeItem?.additionalCostPrice || 0,
            price: sizeItem?.additionalPrice || 0,
            materials: sizeItem?.materials.map((material) => ({
              name: material?.material?.name || "",
              additionalCostPrice: material?.additionalCostPrice || 0,
              price: material?.additionalPrice || 0,
              colors: material.colours.map((colour) => ({
                id: colour.colour.id,
                name: colour.colour.name,
                hex: colour.colour.hexValue,
                additionalCostPrice: colour.additionalCostPrice || 0,
                price: colour.additionalPrice || 0,
                quantity: colour.quantity,
                weight: colour.weight || 0,
                images: [],
                imageUrls: colour?.imageUrls || [],
              })),
            })),
          })),
        };
        setPropertyData(transformedPropertyData);

        // Set initial selections for published
        if (transformedPropertyData?.sizes?.length > 0) {
          setSelectedSize(transformedPropertyData?.sizes[0]?.sizeId);
          if (transformedPropertyData?.sizes[0]?.materials.length > 0) {
            setSelectedMaterial(
              transformedPropertyData?.sizes[0]?.materials[0]?.name,
            );
            if (
              transformedPropertyData?.sizes[0]?.materials[0]?.colors?.length >
              0
            ) {
              setSelectedColor(
                transformedPropertyData?.sizes[0]?.materials[0]?.colors[0].hex,
              );
            }
          }
          setCurrentStep(2);
        }
      }
    }
  }, [product, sizeGuideData, materialsData, colorsData])

  // Validation
  // PE-2099 — sale-mode-aware: CONSULTATION_REQUIRED replaces the price
  // requirement with a linked booking service.
  const isBaseDataValid = () => {
    return !!(formData.name && formData.category && formData.details) && isSaleModeValid(formData)
  }

  const isPublishDataValid = () => {
    return isBaseDataValid() && formData.styleGuide
  }

  const canAddVariants = () => {
    return isBaseDataValid()
  }

  // Mutations
  const updateDraftMutation = useMutation({
    mutationFn: ({ id, data, status }: { id: string; data: any; status: ProductStatus }) => 
      productsApi.updateByStatus(id, data, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      queryClient.invalidateQueries({ queryKey: ['product', productId] })
      navigate('/products')
    }
  })

  const updateProductMutation = useMutation({
    mutationFn: (data: any) => productsApi.update(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      queryClient.invalidateQueries({ queryKey: ['product', productId] })
      navigate('/products')
    }
  })

  const publishDraftMutation = useMutation({
    mutationFn: (id: string) => productsApi.drafts.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      queryClient.invalidateQueries({ queryKey: ['product', productId] })
      navigate('/products')
    }
  })

  // PE-2220 — see useAddProduct: the transform now throws instead of emitting a
  // `"string"` sentinel, so every call site funnels through this guard.
  const buildPayload = (status: 'DRAFT' | 'PUBLISHED') => {
    if (!product) return null
    try {
      return transformToUpdateFormat(
        formData,
        propertyData,
        product,
        materialsData?.data || [],
        colorsData?.data || [],
        status,
      )
    } catch (err) {
      if (err instanceof UnresolvedMetadataError) {
        toast.error(err.message)
        return null
      }
      throw err
    }
  }

  // Actions
  const handleSaveDraft = async () => {
    if (!isBaseDataValid() || !product) return;

    const updateFormData = buildPayload("DRAFT");
    if (!updateFormData) return;

    if (product.status === "DRAFT") {
      updateDraftMutation.mutate({
        id: product.id,
        data: updateFormData,
        status: "DRAFT",
      });
    } else {
      // Convert published product to draft
      updateProductMutation.mutate(updateFormData);
    }
  }

  const handlePublish = async () => {
    if (!isPublishDataValid() || !product) return;

    if (product.status === "DRAFT") {
      // First update the draft with current data, then publish
      const updateFormData = buildPayload("DRAFT");
      if (!updateFormData) return;

      // Update draft first, then publish
      updateDraftMutation.mutate(
        { id: product.id, data: updateFormData, status: "DRAFT" },
        {
          onSuccess: () => {
            publishDraftMutation.mutate(product.id);
          },
        },
      );
    } else {
      const updateFormData = buildPayload("PUBLISHED");
      if (!updateFormData) return;
      updateProductMutation.mutate(updateFormData);
    }
  }

  const handlePublishDraft = async () => {
    if (!product || product.status !== 'DRAFT') return
    publishDraftMutation.mutate(product.id)
  }

  return {
    // Data
    formData, setFormData,
    propertyData, setPropertyData,
    selectedSize, setSelectedSize,
    selectedMaterial, setSelectedMaterial,
    selectedColor, setSelectedColor,
    showPropertiesModal, setShowPropertiesModal,
    currentStep, setCurrentStep,
    
    // API Data
    categories: categoriesData?.data || [],
    apiColors: colorsData?.data || [],
    sizeGuides: sizeGuideData?.result?.data || [],
    materials: materialsData?.data || [],
    collections: collectionsData?.data || [],
    occasions: occasionsData?.data || [],
    
    // Material type from product (handle both schemas)
    materialType: product?.status === 'DRAFT' 
      ? materialsData?.data?.find((m: any) => m.id === (product as any)?.data?.sizes?.[0]?.materials?.[0]?.materialId)?.name || ''
      : product?.sizes?.[0]?.materials?.[0]?.material?.name || '',
    
    // Product
    product,
    
    // Validation
    isBaseDataValid,
    isPublishDataValid,
    canAddVariants,
    
    // Loading
    isLoading: updateDraftMutation.isPending || updateProductMutation.isPending || publishDraftMutation.isPending || isLoadingProduct,
    
    // Actions
    handleSaveDraft,
    handlePublish,
    handlePublishDraft,
    navigate
  }
}